import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { Monitor, Smartphone, Tablet, Download, RefreshCw, AlertCircle } from 'lucide-react'
import { apiService } from '../../services/api'
import { useBranding } from '../../context/BrandingContext'
import LoadingSpinner from '../common/LoadingSpinner'
import '../../styles/admin-pages.css'

// ── SVG chart helpers (1:1 with the prototype's charts.jsx) ───────────────────

function smoothPath(points) {
  if (points.length < 2) return ''
  let d = `M ${points[0][0]} ${points[0][1]}`
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1]
    const [x1, y1] = points[i]
    const cx = (x0 + x1) / 2
    d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`
  }
  return d
}

function AreaChart({ data, height = 120, color = 'var(--accent)', fillId = 'areaFill', strokeWidth = 2, padding = 8 }) {
  if (!data || data.length < 2) return null
  const w = 100
  const h = height
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const points = data.map((v, i) => [
    padding + (i * (w - padding * 2)) / (data.length - 1),
    padding + (1 - (v - min) / range) * (h - padding * 2),
  ])
  const d = smoothPath(points)
  const area = `${d} L ${points[points.length - 1][0]} ${h - padding} L ${points[0][0]} ${h - padding} Z`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${fillId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

function MultiAreaChart({ series, height = 220 }) {
  const w = 100
  const h = height
  const padding = 10
  const valid = series.filter(s => s.data && s.data.length > 1)
  if (!valid.length) return null
  const allMax = Math.max(...valid.flatMap(s => s.data))
  const range = allMax || 1
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        {valid.map((s, i) => (
          <linearGradient key={i} id={`gradS${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity="0.32" />
            <stop offset="100%" stopColor={s.color} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
      {[0.25, 0.5, 0.75].map((g, i) => (
        <line key={i} x1="0" x2={w} y1={padding + g * (h - padding * 2)} y2={padding + g * (h - padding * 2)} stroke="var(--border)" strokeDasharray="1.5 3" strokeWidth="0.4" />
      ))}
      {valid.map((s, sIdx) => {
        const points = s.data.map((v, i) => [
          padding + (i * (w - padding * 2)) / (s.data.length - 1),
          padding + (1 - v / range) * (h - padding * 2),
        ])
        const d = smoothPath(points)
        const area = `${d} L ${points[points.length - 1][0]} ${h - padding} L ${points[0][0]} ${h - padding} Z`
        return (
          <g key={sIdx}>
            <path d={area} fill={`url(#gradS${sIdx})`} opacity="0.7" />
            <path d={d} fill="none" stroke={s.color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
          </g>
        )
      })}
    </svg>
  )
}

function Donut({ data, size = 130, thickness = 16, centerLabel, centerValue }) {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (!total) return null
  const r = (size - thickness) / 2
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={thickness} />
        {data.map((d, i) => {
          const len = (d.value / total) * c
          const seg = (
            <circle
              key={i}
              cx={size / 2} cy={size / 2} r={r}
              fill="none" stroke={d.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          )
          offset += len + 2
          return seg
        })}
      </svg>
      {(centerValue || centerLabel) && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{centerValue}</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{centerLabel}</div>
        </div>
      )}
    </div>
  )
}

// ── KPI stat card (matches the prototype's AnalyticsStat) ─────────────────────
function AnalyticsStat({ label, value, series }) {
  return (
    <div className="card card-pad-sm" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>
        {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
      </div>
      <div style={{ height: 28, margin: '0 -4px' }}>
        {series && series.length > 1
          ? <AreaChart data={series} height={28} color="var(--accent)" fillId={`a-${label.replace(/\s+/g, '')}`} strokeWidth={1.2} padding={2} />
          : null}
      </div>
    </div>
  )
}

function Legend({ color, label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-muted)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 3, background: color }} />
        {label}
      </div>
      <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</span>
    </div>
  )
}

const EmptyState = ({ children }) => (
  <div style={{ fontSize: 13, color: 'var(--text-faint)', textAlign: 'center', padding: '28px 0' }}>{children}</div>
)

const compact = (n) => {
  if (n == null) return '—'
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}m`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return `${n}`
}

const DEVICE_ICON = { Desktop: Monitor, Mobile: Smartphone, Tablet }

// ── Main component ────────────────────────────────────────────────────────────
const AnalyticsDashboard = () => {
  const { cmsName } = useBranding()
  const [dateRange, setDateRange] = useState('30d')

  const { data: analytics, isLoading, isFetching, isError, refetch } = useQuery(
    ['analytics', dateRange],
    () => apiService.get(`/analytics/admin/overview?range=${dateRange}`),
    { staleTime: 120000, refetchInterval: 600000, retry: 2, keepPreviousData: true }
  )

  const displayData = analytics?.data || analytics

  const handleExport = () => {
    if (!displayData) return
    const { overview, activityData = [], topPages = [], referrerData = [] } = displayData
    const rows = [
      [`${cmsName} Analytics Export`, `Range: ${dateRange}`, `Generated: ${new Date().toLocaleString()}`],
      [],
      ['=== OVERVIEW ==='],
      ['Metric', 'Value'],
      ['Published Sites', overview?.publishedSites ?? 0],
      ['Total Clients', overview?.totalClients ?? 0],
      ['Page Views', overview?.totalPageViews ?? 0],
      ['Unique Visitors', overview?.uniqueVisitors ?? 0],
      ['Edits in Period', overview?.editsInPeriod ?? 0],
      [],
      ['=== ACTIVITY OVER TIME ==='],
      ['Period', 'Edits', 'Publishes', 'Page Views'],
      ...activityData.map(r => [r.name, r.edits, r.publishes, r.views ?? 0]),
      [],
      ['=== TRAFFIC SOURCES ==='],
      ['Source', 'Visits', '% of Total'],
      ...referrerData.map(r => [r.source, r.count, r.percentage + '%']),
      [],
      ['=== TOP PAGES ==='],
      ['Page', 'Views', '% of Total'],
      ...topPages.map(p => [p.page, p.views, p.percentage + '%']),
    ]
    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `orbith-analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <LoadingSpinner size="lg" text="Loading analytics..." />
      </div>
    )
  }

  if (isError || !displayData) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, gap: 12 }}>
        <span style={{ color: 'var(--text-faint)' }}><AlertCircle size={32} /></span>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Failed to load analytics. Check your connection and try again.</p>
        <button className="btn" onClick={() => refetch()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    )
  }

  const {
    overview,
    activityData = [],
    topPages = [],
    deviceTypes = [],
    referrerData = [],
  } = displayData

  const viewsSeries = activityData.map(d => d.views ?? 0)
  const editsSeries = activityData.map(d => d.edits ?? 0)

  const deviceDonut = deviceTypes.map(d => ({ value: d.count ?? 0, color: d.color || 'var(--accent)' }))

  const axisLabels = activityData.length
    ? [0, Math.floor(activityData.length / 4), Math.floor(activityData.length / 2), Math.floor(activityData.length * 3 / 4), activityData.length - 1]
        .map(i => activityData[i]?.name).filter(Boolean)
    : []

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Analytics</div>
          <h1>Analytics</h1>
          <div className="subtitle">
            {isFetching
              ? 'Updating…'
              : displayData.lastUpdated
              ? `Cross-site performance · last updated ${new Date(displayData.lastUpdated).toLocaleTimeString()}`
              : 'Cross-site performance'}
          </div>
        </div>
        <div className="flex gap-2 items-center header-actions">
          <div className="tabs">
            {[['7d', '7d'], ['30d', '30d'], ['90d', '90d'], ['1y', 'YTD']].map(([v, l]) => (
              <button key={v} className={dateRange === v ? 'active' : ''} onClick={() => setDateRange(v)}>{l}</button>
            ))}
          </div>
          {/* Labels collapse to icon-only on phones (.btn-label hides ≤768) to keep
              the cluster on one tidy row instead of running off-screen. */}
          <button className="btn" onClick={() => refetch()} disabled={isFetching} title="Refresh" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} style={isFetching ? { animation: 'spin 1s linear infinite' } : undefined} /> <span className="btn-label">Refresh</span>
          </button>
          <button className="btn" onClick={handleExport} title="Export CSV" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> <span className="btn-label">Export</span>
          </button>
        </div>
      </div>

      <div className="page-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Top stats */}
        <div className="bento stagger-in" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
          <AnalyticsStat label="Visits" value={overview?.totalPageViews} series={viewsSeries} />
          <AnalyticsStat label="Unique visitors" value={overview?.uniqueVisitors} />
          <AnalyticsStat label="Published sites" value={overview?.publishedSites} />
          <AnalyticsStat label="Total clients" value={overview?.totalClients} />
          <AnalyticsStat label="Edits" value={overview?.editsInPeriod} series={editsSeries} />
        </div>

        {/* Big chart */}
        <div className="card card-pad">
          <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
            <div>
              <h2 className="h2" style={{ fontSize: 16 }}>Traffic over time</h2>
              <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Page views &amp; edits across all live sites · {dateRange}</div>
            </div>
          </div>
          {activityData.length > 1 ? (
            <>
              <div style={{ display: 'flex', gap: 24, marginBottom: 14 }}>
                <Legend color="var(--accent)" label="Page views" value={(overview?.totalPageViews ?? 0).toLocaleString()} />
                <Legend color="var(--text-faint)" label="Edits" value={(overview?.editsInPeriod ?? 0).toLocaleString()} />
              </div>
              <MultiAreaChart
                height={260}
                series={[
                  { color: 'var(--accent)', data: viewsSeries },
                  { color: 'var(--text-faint)', data: editsSeries },
                ]}
              />
              {axisLabels.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-faint)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
                  {axisLabels.map((n, i) => <span key={i}>{n}</span>)}
                </div>
              )}
            </>
          ) : (
            <EmptyState>No activity recorded for this period yet.</EmptyState>
          )}
        </div>

        {/* Sources + Devices + Top pages */}
        <div className="bento" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 14, alignItems: 'stretch' }}>
          {/* Traffic sources */}
          <div className="card card-pad">
            <h2 className="h2" style={{ fontSize: 16 }}>Traffic sources</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 14 }}>Where visitors come from</div>
            {referrerData.length ? referrerData.map((s, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color || 'var(--accent)', marginRight: 8, flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, flex: 1 }}>{s.source}</span>
                  <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-soft)' }}>{(s.count || 0).toLocaleString()}</span>
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', width: 36, textAlign: 'right' }}>{s.percentage}%</span>
                </div>
                <div className="progress thin"><div style={{ width: `${s.percentage}%`, background: s.color || 'var(--accent)' }} /></div>
              </div>
            )) : <EmptyState>No referrer data yet.</EmptyState>}
          </div>

          {/* Devices */}
          <div className="card card-pad">
            <h2 className="h2" style={{ fontSize: 16 }}>Devices</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 18 }}>Visitor breakdown</div>
            {deviceDonut.length ? (
              <div className="flex items-center gap-4">
                <Donut size={140} thickness={18} data={deviceDonut} centerLabel="visits" centerValue={compact(overview?.totalPageViews)} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {deviceTypes.map((d, i) => {
                    const DeviceIcon = DEVICE_ICON[d.name] || Monitor
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <DeviceIcon size={14} style={{ color: d.color || 'var(--accent)' }} />
                        <span style={{ color: 'var(--text-soft)' }}>{d.name}</span>
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>{d.value}%</span>
                        <span style={{ color: 'var(--text-faint)', fontSize: 11, fontFamily: 'var(--font-mono)', width: 48, textAlign: 'right' }}>{compact(d.count)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : <EmptyState>No device data yet.</EmptyState>}
          </div>

          {/* Top pages */}
          <div className="card card-pad">
            <h2 className="h2" style={{ fontSize: 16 }}>Top pages</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 14 }}>Most-viewed pages</div>
            {topPages.length ? (
              <div className="sites-table" style={{ border: 'none', background: 'transparent' }}>
                <div className="row header" style={{ gridTemplateColumns: '2fr 1fr 1fr', paddingLeft: 0, paddingRight: 0 }}>
                  <div>Page</div><div style={{ textAlign: 'right' }}>Views</div><div style={{ textAlign: 'right' }}>Share</div>
                </div>
                {topPages.slice(0, 6).map((p, i) => (
                  <div className="row" key={i} style={{ gridTemplateColumns: '2fr 1fr 1fr', paddingLeft: 0, paddingRight: 0 }}>
                    <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.page}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{(p.views || 0).toLocaleString()}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', textAlign: 'right', color: 'var(--text-muted)' }}>{p.percentage}%</div>
                  </div>
                ))}
              </div>
            ) : <EmptyState>No page views yet.</EmptyState>}
          </div>
        </div>
      </div>
    </>
  )
}

export default AnalyticsDashboard
